'use client'

import { useState, useContext } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Separator } from '@/components/ui/shadcn/separator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/shadcn/dialog'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { AlertCircle } from 'lucide-react'
import { fetcher } from '@/lib/api/fetch'
import { AuthContext } from '@/contexts/AuthContext'
import { ManagerContext } from '@/contexts/ManagerContext'

export default function ConfirmOrganization({ open, setOpen, onDelete }) {
  const authContext = useContext(AuthContext)
  const managerContext = useContext(ManagerContext)
  const [error, setError] = useState(null)

  const submit = async e => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const org = formData.get('organization')

    // Check the email address address matches the user's email address
    if (org !== authContext.user.organization.name) {
      setError('Organization name does not match')
      return
    }

    // Call the API to remove the user
    const res = await fetcher(
      '/api/admin/organization/' + authContext.user.organization.id,
      { method: 'DELETE' },
      authContext,
    )

    // If the API call was successful, sign the user out
    if (res.ok) {
      authContext.setuser(
        {
          ...authContext.user,
          organization: null,
        },
        () => {
          setOpen(false)

          // Remove this organization from the orgs list in manager context
          managerContext.removeOrganization(authContext.user.organization.id)

          onDelete()
        },
      )
    } else {
      // If the API call failed, show the error
      const data = await res.json()
      setError(data.message)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) setOpen(false)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Remove {authContext.user.organization.name}</DialogTitle>
          <DialogDescription>
            Removing the organization will also remove all data related to this
            organization. Once deleted this action cannot be reversed.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm mt-2 mb-2">
          To confirm the removal of this organization type the full name:{' '}
          <b>{authContext.user.organization.name}</b>
        </p>
        <Separator />
        {error && (
          <Alert variant="destructive" className="my-2">
            <AlertCircle className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={submit}>
          <div className="space-y-2 mb-4">
            <Label htmlFor="organization">Organization Name</Label>
            <Input id="organization" name="organization" required />
          </div>
          <Button type="submit" variant="destructive" className="w-full">
            Remove Organization
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full mt-2"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
